// =====================================================================
// Top-level router.
// ---------------------------------------------------------------------
// Routes:
//   /              → Landing (public, eager, almost every visitor hits this)
//   /paper         → Preprint iframe (public, lazy)
//   /login         → Auth (public, lazy)
//   /portal/*      → User dashboard (protected, lazy)
//   /admin/*       → Master console (protected + admin role, lazy)
//
// Everything except Landing is dynamic-imported via React.lazy so the
// initial bundle stays minimal. Vite turns each lazy() call into its
// own chunk; the router fetches the chunk only when that route is
// first visited.
// ---------------------------------------------------------------------
// Perf contract:
//   • Landing renders synchronously, with NO blocking on auth. Even
//     though <AuthProvider> starts in loading=true, we don't gate the
//     <Routes> tree on it, only ProtectedRoute / AdminRoute do.
//   • The boot screen (spinner) is gone from the happy path; you only
//     see it if a protected route is hit before auth resolves.
// =====================================================================

import { lazy, Suspense, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "./context/AuthContext.jsx";

// Landing stays eager, it's the only page that matters for the cold
// first-paint number we keep tuning.
import Landing from "./pages/Landing.jsx";

import ProtectedRoute from "./components/ProtectedRoute.jsx";
import AdminRoute from "./components/AdminRoute.jsx";
import Spinner from "./components/Spinner.jsx";
import CookieBanner from "./components/CookieBanner.jsx";

// Lazy public pages, these don't need to ship in the landing bundle.
const Login          = lazy(() => import("./pages/Login.jsx"));
const PrivacyPolicy  = lazy(() => import("./pages/legal/PrivacyPolicy.jsx"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService.jsx"));
const Changelog      = lazy(() => import("./pages/Changelog.jsx"));
const Pricing        = lazy(() => import("./pages/Pricing.jsx"));
const Playground     = lazy(() => import("./pages/Playground.jsx"));
const CostAnalysis   = lazy(() => import("./pages/CostAnalysis.jsx"));
const Benchmarks     = lazy(() => import("./pages/Benchmarks.jsx"));
const Sla            = lazy(() => import("./pages/Sla.jsx"));
const NotFound       = lazy(() => import("./pages/NotFound.jsx"));

// Lazy portal, fully separate chunk; never touches the landing bundle.
const PortalLayout    = lazy(() => import("./pages/portal/PortalLayout.jsx"));
const PortalOverview  = lazy(() => import("./pages/portal/Overview.jsx"));
const ConnectionSetup = lazy(() => import("./pages/portal/ConnectionSetup.jsx"));
const Billing         = lazy(() => import("./pages/portal/Billing.jsx"));
const Keys            = lazy(() => import("./pages/portal/Keys.jsx"));
const Account         = lazy(() => import("./pages/portal/Account.jsx"));

// Lazy admin, even more rarely loaded.
const AdminLayout    = lazy(() => import("./pages/admin/AdminLayout.jsx"));
const AdminOverview  = lazy(() => import("./pages/admin/AdminOverview.jsx"));
const Revenue        = lazy(() => import("./pages/admin/Revenue.jsx"));
const Telemetry      = lazy(() => import("./pages/admin/Telemetry.jsx"));
const Customers      = lazy(() => import("./pages/admin/Customers.jsx"));
const BillingRuns    = lazy(() => import("./pages/admin/BillingRuns.jsx"));

function RouteFallback() {
  return (
    <div className="boot-screen" style={{ flexDirection: "column", gap: 18 }}>
      <img
        src="/icon.svg"
        alt=""
        width="36"
        height="36"
        style={{ opacity: 0.9 }}
      />
      <Spinner size={18} />
    </div>
  );
}

// Scroll to the top on every route change. Without this the SPA keeps
// the previous page's scroll position on internal navigations (classic
// react-router behavior), so e.g. tapping a footer link landed the
// visitor mid-page on the destination. Hash links (#proof) are left to
// the browser's native anchor behavior.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  const { loading, isAuthenticated, isAdmin, profileVerified } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Toggle <body class="saas-mode"> on dashboard / auth routes so
  // global CSS picks up the dashboard styles. Subscribed to
  // location.pathname rather than `popstate` because react-router
  // performs pushState navigations that don't fire popstate.
  useEffect(() => {
    const p = location.pathname;
    const saas =
      p.startsWith("/portal") || p.startsWith("/admin") || p === "/login";
    document.body.classList.toggle("saas-mode", saas);
  }, [location.pathname]);

  // Post-OAuth landing watcher. The "lm-oauth-pending" flag is set in
  // Login.jsx right before signInWithOAuth and consumed here once the
  // session has actually resolved. Ensures the user lands on /portal
  // (or /admin) regardless of where Supabase's Redirect URL config
  // chose to drop them after the round-trip.
  //
  // CRITICAL: gate on `profileVerified`, not just `loading=false`. The
  // `loading` flag flips to false as soon as the SESSION resolves, but
  // the PROFILE row (which carries the admin role) is still being
  // fetched in the background at that moment. If we routed on the
  // first non-loading tick, isAdmin would be false (synthetic-profile
  // default) and the admin would land on /portal. Waiting for
  // profileVerified=true guarantees `isAdmin` reflects the real DB
  // value before we make the routing decision.
  useEffect(() => {
    if (loading || !isAuthenticated) return;
    if (!profileVerified) return;
    if (sessionStorage.getItem("lm-oauth-pending") !== "1") return;
    sessionStorage.removeItem("lm-oauth-pending");
    const dest = isAdmin ? "/admin" : "/portal";
    if (!location.pathname.startsWith(dest)) {
      navigate(dest, { replace: true });
    }
  }, [loading, isAuthenticated, profileVerified, isAdmin, location.pathname, navigate]);

  // NOTE: we intentionally do NOT gate the router on `loading`. The
  // landing page renders the moment React mounts; ProtectedRoute and
  // AdminRoute handle the spinner for auth-gated pages.
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/playground" element={<Playground />} />
          <Route path="/cost-analysis" element={<CostAnalysis />} />
          <Route path="/benchmarks" element={<Benchmarks />} />
          <Route path="/sla" element={<Sla />} />

          <Route
            path="/portal"
            element={
              <ProtectedRoute>
                <PortalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<PortalOverview />} />
            <Route path="connection" element={<ConnectionSetup />} />
            <Route path="billing" element={<Billing />} />
            <Route path="keys" element={<Keys />} />
            <Route path="account" element={<Account />} />
          </Route>

          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<Navigate to="revenue" replace />} />
            <Route path="overview" element={<AdminOverview />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="telemetry" element={<Telemetry />} />
            <Route path="customers" element={<Customers />} />
            <Route path="billing-runs" element={<BillingRuns />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <CookieBanner />
    </>
  );
}
